import { apiService, type Document as ApiDocument } from './api';
import { getCompanyShortName, companyShortNames } from '../utils/companyNames';
import { formatDate } from '../utils/driveFormatters';
import { parseSavedMessageMarkdown } from '../utils/savedMessageMarkdown';
import { serializeSnippetToMarkdown, parseSnippetMarkdown } from '../utils/snippetMarkdown';
import type { PDFSnippet, SavedMessage } from './api';
import type { SavedContent } from './savedContentService';
import { CATEGORY_LABELS } from '../constants/driveConstants';

type DriveSnippet = PDFSnippet & {
  markdown?: string;
  front_matter?: Record<string, any>;
  parsed_content?: string;
};

export interface TableItem {
  id: string;
  type: 'document' | 'saved-message' | 'snippet' | 'studio-result';
  category: 'compartido' | 'privado' | 'snippet' | 'mensaje' | 'studio';
  name: string;
  date: string;
  dateValue: Date;
  size: string;
  sizeValue: number;
  pages: number;
  chunks: number;
  docType: string;
  company: string;
  status: string;
  origin: 'private' | 'shared';
  companyName: string;
  originalCompanyName?: string;
  documentType: string;
  documentPeriod: string;
  originalPeriod?: string;
  documentCode: string;
  processingTimeSeconds: number | null;
  processingMethods: string[];
  data: ApiDocument | SavedMessage | DriveSnippet | SavedContent;
}

/**
 * Extract period from filename
 */
export const extractPeriodFromFilename = (filename: string): string | null => {
  if (!filename) return null;

  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  const tempName = nameWithoutExt.replace(/ S\.A\./g, ' SA').replace(/ S\.p\.A\./g, ' SPA');
  const parts = tempName.split('_');
  const knownTypes = ['ANALISIS', 'FECU'];

  if (parts.length >= 2) {
    const typeIdx = parts.findIndex((part) => knownTypes.includes(part));
    if (typeIdx > 0 && parts.length > typeIdx + 1) {
      return parts[typeIdx + 1];
    }
  }

  return null;
};

/**
 * Update company short names for all documents
 */
export const updateCompanyShortNames = async (
  privateDocuments: ApiDocument[],
  sharedDocuments: ApiDocument[],
  originalCompanyNames: Record<string, string>,
  loadPrivateDocuments: () => Promise<void>,
  loadSharedDocuments: () => Promise<void>
): Promise<void> => {
  try {
    const allDocs = [...privateDocuments, ...sharedDocuments];

    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    for (const doc of allDocs) {
      const originalCompanyName = originalCompanyNames[doc.id] || doc.company_name || '';
      if (!originalCompanyName) {
        skippedCount++;
        continue;
      }

      const shortName = getCompanyShortName(originalCompanyName);
      if (!shortName) {
        skippedCount++;
        continue;
      }

      const currentShortName = (doc.company_name || '').split(' - ')[0].trim();
      if (currentShortName === shortName) {
        skippedCount++;
        continue;
      }

      try {
        await apiService.updateDocument(doc.id, {
          company_name: shortName,
        });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`❌ Error actualizando ${doc.id}:`, error);
      }
    }

    await Promise.all([loadPrivateDocuments(), loadSharedDocuments()]);

    alert(
      `✅ Actualización completada:\n- Exitosos: ${successCount}\n- Errores: ${errorCount}\n- Omitidos: ${skippedCount}`
    );
  } catch (error) {
    console.error('❌ Error en actualización masiva:', error);
      alert('❌ Error al actualizar nombres cortos de empresas');
    }
  };

/**
 * Transform documents, messages, snippets, and studio results into table items
 */
export const transformToTableItems = (
  privateDocuments: ApiDocument[],
  sharedDocuments: ApiDocument[],
  savedMessages: SavedMessage[],
  snippets: DriveSnippet[],
  studioResults: SavedContent[],
  originalCompanyNames: Record<string, string> = {},
  originalPeriods: Record<string, string> = {}
): TableItem[] => {
  const items: TableItem[] = [];

  // Add private documents
  privateDocuments
    .filter((doc) => doc.scope !== 'shared')
    .forEach((doc) => {
      const extendedCompanyName = originalCompanyNames[doc.id] || doc.company_name || '-';

      items.push({
        id: doc.id,
        type: 'document',
        category: 'privado',
        name: doc.name || doc.original_filename,
        date: formatDate(doc.created_at),
        dateValue: new Date(doc.created_at),
        size: doc.file_size_human || `${Math.floor(doc.file_size / 1024)} KB`,
        sizeValue: doc.file_size || 0,
        pages: doc.page_count || 0,
        chunks: doc.total_chunks || 0,
        docType: doc.document_type || 'PDF',
        company: doc.company_name || '-',
        status:
          doc.processing_status === 'completed'
            ? 'Completado'
            : doc.processing_status === 'processing'
            ? 'Procesando'
            : doc.processing_status === 'failed'
            ? 'Error'
            : 'Pendiente',
        origin: 'private',
        companyName: extendedCompanyName,
        originalCompanyName: originalCompanyNames[doc.id] || doc.company_name || undefined,
        documentType: doc.document_type || '-',
        documentPeriod: doc.document_period || '-',
        originalPeriod: originalPeriods[doc.id] || doc.document_period || undefined,
        documentCode: doc.document_code || '-',
        processingTimeSeconds: doc.processing_time_seconds ?? null,
        processingMethods: Array.isArray(doc.processing_methods) ? doc.processing_methods : [],
        data: doc,
      });
    });

  // Add shared documents
  sharedDocuments.forEach((doc) => {
    const extendedCompanyName = originalCompanyNames[doc.id] || doc.company_name || '-';

    items.push({
      id: doc.id,
      type: 'document',
      category: 'compartido',
      name: doc.name || doc.original_filename,
      date: formatDate(doc.created_at),
      dateValue: new Date(doc.created_at),
      size: doc.file_size_human || `${Math.floor(doc.file_size / 1024)} KB`,
      sizeValue: doc.file_size || 0,
      pages: doc.page_count || 0,
      chunks: doc.total_chunks || 0,
      docType: doc.document_type || 'PDF',
      company: doc.company_name || '-',
      status:
        doc.processing_status === 'completed'
          ? 'Completado'
          : doc.processing_status === 'processing'
          ? 'Procesando'
          : doc.processing_status === 'failed'
          ? 'Error'
          : 'Pendiente',
      origin: 'shared',
      companyName: extendedCompanyName,
      originalCompanyName: originalCompanyNames[doc.id] || doc.company_name || undefined,
      documentType: doc.document_type || '-',
      documentPeriod: doc.document_period || '-',
      originalPeriod: originalPeriods[doc.id] || doc.document_period || undefined,
      documentCode: doc.document_code || '-',
      processingTimeSeconds: doc.processing_time_seconds ?? null,
      processingMethods: Array.isArray(doc.processing_methods) ? doc.processing_methods : [],
      data: doc,
    });
  });

  // Add saved messages
  savedMessages.forEach((msg) => {
    const parsed = parseSavedMessageMarkdown(msg.content);
    const metadata = parsed.data as Record<string, any>;
    const displayName = metadata.title || msg.title || 'Mensaje guardado';

    items.push({
      id: msg.id,
      type: 'saved-message',
      category: 'mensaje',
      name: displayName,
      date: formatDate(msg.created_at ? new Date(msg.created_at) : new Date()),
      dateValue: new Date(msg.created_at || Date.now()),
      size: '-',
      sizeValue: 0,
      pages: 0,
      chunks: 0,
      docType: 'Mensaje',
      company: '-',
      status: 'Completado',
      origin: 'private',
      companyName: '-',
      documentType: '-',
      documentPeriod: '-',
      documentCode: '-',
      processingTimeSeconds: null,
      processingMethods: [],
      data: {
        ...msg,
        content: parsed.content,
        markdown: msg.content,
        metadata,
      } as SavedMessage & { markdown?: string; metadata?: Record<string, any> },
    });
  });

  // Add snippets
  snippets.forEach((snippet) => {
    const serialized = snippet.markdown
      ? {
          markdown: snippet.markdown,
          metadata: snippet.front_matter ?? (parseSnippetMarkdown(snippet.markdown).data as Record<string, any>),
          content: snippet.parsed_content ?? parseSnippetMarkdown(snippet.markdown).content,
        }
      : serializeSnippetToMarkdown(snippet as any);
    const { markdown, metadata, content } = serialized;
    items.push({
      id: snippet.id,
      type: 'snippet',
      category: 'snippet',
      name: metadata.title || snippet.title || `Snippet - Página ${snippet.page_number}`,
      date: formatDate(snippet.created_at ? new Date(snippet.created_at) : new Date()),
      dateValue: new Date(snippet.created_at || Date.now()),
      size: '-',
      sizeValue: 0,
      pages: 1,
      chunks: 0,
      docType: 'Snippet',
      company: '-',
      status: 'Completado',
      origin: 'private',
      companyName: '-',
      documentType: '-',
      documentPeriod: '-',
      documentCode: '-',
      processingTimeSeconds: null,
      processingMethods: [],
      data: {
        ...snippet,
        markdown,
        front_matter: metadata,
        parsed_content: content,
      },
    });
  });

  // Add studio results
  studioResults.forEach((result) => {
    const createdAt = result.created_at ? new Date(result.created_at) : new Date();
    items.push({
      id: result.id,
      type: 'studio-result',
      category: 'studio',
      name: result.title || result.studio_name || 'Resultado Studio',
      date: formatDate(createdAt),
      dateValue: createdAt,
      size: '-',
      sizeValue: 0,
      pages: 0,
      chunks: 0,
      docType: 'Studio',
      company: result.studio_name || '-',
      status: 'Generado',
      origin: 'private',
      companyName: result.studio_name || '-',
      documentType: result.studio_type || 'Studio',
      documentPeriod:
        (result.meta_data?.period as string) || (result.meta_data?.periodo as string) || '-',
      documentCode: result.studio_id || result.id,
      processingTimeSeconds: null,
      processingMethods: [],
      data: result,
    });
  });

  return items;
};

/**
 * Get unique values for filters
 */
export const getUniqueFilterValues = (
  privateDocuments: ApiDocument[],
  sharedDocuments: ApiDocument[]
) => {
  const uniqueDocTypes = new Set<string>();
  const uniqueCompanies = new Set<string>();
  const uniquePeriods = new Set<string>();

  [...privateDocuments, ...sharedDocuments].forEach((doc) => {
    if (doc.document_type) uniqueDocTypes.add(doc.document_type);
    if (doc.company_name) uniqueCompanies.add(doc.company_name);
    if (doc.document_period) uniquePeriods.add(doc.document_period);
  });

  return {
    uniqueDocTypes: Array.from(uniqueDocTypes).sort(),
    uniqueCompanies: Array.from(uniqueCompanies).sort(),
    uniquePeriods: Array.from(uniquePeriods).sort(),
  };
};

export interface DriveTableFilters {
  filterItemType: string;
  filterPeriod: string;
  filterDocType: string;
  filterCompany: string | string[];
  debouncedSearchTerm: string;
  searchInContent: boolean;
}

interface BuildDriveTableItemsParams {
  baseItems: TableItem[];
  filters: DriveTableFilters;
  sortItems: (items: TableItem[], selectedIds: string[]) => TableItem[];
  internalSelectedDocuments: string[];
}

const matchesSavedMessage = (item: TableItem, query: string): boolean => {
  const msg = item.data as SavedMessage & { markdown?: string; metadata?: Record<string, any> };
  const markdownSource = msg.markdown ?? msg.content;
  const parsed = parseSavedMessageMarkdown(markdownSource);
  const metadata = parsed.data as Record<string, any>;
  const baseContent = parsed.content || msg.content;
  return (
    (baseContent && baseContent.toLowerCase().includes(query)) ||
    (msg.notes && msg.notes.toLowerCase().includes(query)) ||
    (msg.tags && msg.tags.some(tag => tag.toLowerCase().includes(query))) ||
    (metadata && Object.values(metadata).some((value) => {
      if (typeof value === 'string') {
        return value.toLowerCase().includes(query);
      }
      if (Array.isArray(value)) {
        return value.some(entry => String(entry).toLowerCase().includes(query));
      }
      return false;
    }))
  );
};

const matchesSnippet = (item: TableItem, query: string): boolean => {
  const snippet = item.data as PDFSnippet & { markdown?: string; parsed_content?: string; front_matter?: Record<string, any> };
  const baseContent = snippet.parsed_content || snippet.text_content || snippet.context_text || '';
  const parsed = parseSnippetMarkdown(snippet.markdown);
  const metadata = parsed.data as Record<string, any>;
  return (
    (baseContent && baseContent.toLowerCase().includes(query)) ||
    (snippet.notes && snippet.notes.toLowerCase().includes(query)) ||
    (snippet.document_name && snippet.document_name.toLowerCase().includes(query)) ||
    (snippet.document_filename && snippet.document_filename.toLowerCase().includes(query)) ||
    (snippet.tags && snippet.tags.some(tag => tag.toLowerCase().includes(query))) ||
    (metadata && Object.values(metadata).some((value) => {
      if (typeof value === 'string') {
        return value.toLowerCase().includes(query);
      }
      if (Array.isArray(value)) {
        return value.some(entry => String(entry).toLowerCase().includes(query));
      }
      return false;
    }))
  );
};

const matchesStudioResult = (item: TableItem, query: string): boolean => {
  const studioResult = item.data as SavedContent;
  const contentValue = (() => {
    if (!studioResult.content) return '';
    if (typeof studioResult.content === 'string') {
      return studioResult.content;
    }
    try {
      return JSON.stringify(studioResult.content, null, 2);
    } catch (error) {
      console.warn('No se pudo serializar el resultado de Studio para búsqueda:', error);
      return String(studioResult.content);
    }
  })();

  const titleMatch = studioResult.title?.toLowerCase().includes(query) ?? false;
  const nameMatch = studioResult.studio_name?.toLowerCase().includes(query) ?? false;
  const notesMatch = typeof studioResult.meta_data?.notes === 'string'
    ? studioResult.meta_data.notes.toLowerCase().includes(query)
    : false;

  return (
    titleMatch ||
    nameMatch ||
    notesMatch ||
    contentValue.toLowerCase().includes(query)
  );
};

const matchesDocument = (item: TableItem, query: string, searchInContent: boolean): boolean => {
  if (searchInContent) {
    return true;
  }
  const doc = item.data as ApiDocument;
  return !!(
    (doc.company_name && doc.company_name.toLowerCase().includes(query)) ||
    (doc.document_type && doc.document_type.toLowerCase().includes(query)) ||
    (doc.document_period && doc.document_period.toLowerCase().includes(query)) ||
    (doc.document_code && doc.document_code.toLowerCase().includes(query)) ||
    (doc.original_filename && doc.original_filename.toLowerCase().includes(query))
  );
};

export const applyDriveFilters = (items: TableItem[], filters: DriveTableFilters): TableItem[] => {
  let filteredItems = items;

  if (filters.filterItemType !== 'all') {
    if (filters.filterItemType === 'shared') {
      filteredItems = filteredItems.filter(item => item.type === 'document' && item.origin === 'shared');
    } else if (filters.filterItemType === 'private') {
      filteredItems = filteredItems.filter(item => item.type === 'document' && item.origin === 'private');
    } else {
      filteredItems = filteredItems.filter(item => item.type === filters.filterItemType);
    }
  }

  if (filters.filterPeriod !== 'all') {
    filteredItems = filteredItems.filter(item => {
      if (item.type === 'document') {
        const doc = item.data as ApiDocument;
        return doc.document_period === filters.filterPeriod;
      }
      return false;
    });
  }

  if (filters.filterDocType !== 'all') {
    filteredItems = filteredItems.filter(item => item.docType?.toLowerCase() === filters.filterDocType.toLowerCase());
  }

  if (filters.filterCompany !== 'all') {
    const companies = Array.isArray(filters.filterCompany) ? filters.filterCompany : [filters.filterCompany];
    filteredItems = filteredItems.filter(item => {
      return companies.some(company => item.company?.toLowerCase() === company.toLowerCase());
    });
  }

  if (filters.debouncedSearchTerm) {
    const query = filters.debouncedSearchTerm.toLowerCase().trim();
    filteredItems = filteredItems.filter((item) => {
      const basicMatch = item.name.toLowerCase().includes(query);
      if (basicMatch) return true;

      const categoryMatch =
        CATEGORY_LABELS[item.category].toLowerCase().includes(query) ||
        item.category.includes(query);
      if (categoryMatch) return true;

      if (item.type === 'document') {
        return matchesDocument(item, query, filters.searchInContent);
      }

      if (item.type === 'saved-message') {
        return matchesSavedMessage(item, query);
      }

      if (item.type === 'snippet') {
        return matchesSnippet(item, query);
      }

      if (item.type === 'studio-result') {
        return matchesStudioResult(item, query);
      }

      return false;
    });
  }

  return filteredItems;
};

export const buildDriveTableItems = ({
  baseItems,
  filters,
  sortItems,
  internalSelectedDocuments,
}: BuildDriveTableItemsParams) => {
  const filteredItems = applyDriveFilters(baseItems, filters);
  return sortItems(filteredItems, internalSelectedDocuments);
};




